import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Card, Row, Col, Statistic, Spin, Alert, Input, 
  Avatar, Tag, Divider, Typography, Empty
} from 'antd';
import { 
  PhoneOutlined, MessageOutlined, StarOutlined, 
  SearchOutlined, TeamOutlined, FireOutlined 
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Search } = Input;

function Dashboard() {

  const [callers, setCallers] = useState([]);
  const [filteredCallers, setFilteredCallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const navigate = useNavigate();

  useEffect(() => {

    const fetchData = async () => {

      try {

        setLoading(true);

        const authToken = localStorage.getItem('authToken');

        const [callStatsRes, smsStatsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/stats`, {
            headers: {
              Authorization: `Bearer ${authToken}`
            }
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/sms-stats`, {
            headers: {
              Authorization: `Bearer ${authToken}`
            }
          })
        ]);

        const callerStats = {};
        const uniqueCallers = [];

        callStatsRes.data.forEach(row => {

          if (!callerStats[row.caller_name]) {

            callerStats[row.caller_name] = {
              calls: 0,
              sms: 0,
              isTopPerformer: false
            };
            uniqueCallers.push(row.caller_name);

          }

          callerStats[row.caller_name].calls += row.total_calls;

        });

        smsStatsRes.data.forEach(row => {

          if (callerStats[row.sender_name]) {

            callerStats[row.sender_name].sms += row.total_messages;

          }

        });

        const sortedCallers = uniqueCallers.sort((a, b) => {

          const totalA = callerStats[a].calls + callerStats[a].sms;
          const totalB = callerStats[b].calls + callerStats[b].sms;
          return totalB - totalA;

        });

        const topThreshold = Math.floor(sortedCallers.length * 0.2);
        sortedCallers.slice(0, topThreshold).forEach(name => {
          callerStats[name].isTopPerformer = true;
        });

        let visibleCallers = null;
        
        const filtered = visibleCallers
          ? sortedCallers.filter(name => visibleCallers.has(name))
          : sortedCallers;
        
        setCallers(filtered);
        setFilteredCallers(filtered);
        setStats(callerStats);

      } catch (err) {

        setError('Error al cargar los datos de agentes');

      } finally {

        setLoading(false);

      }

    };

    fetchData();

  }, []);

  const handleSearch = (value) => {

    const filtered = callers.filter(name =>
      name.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredCallers(filtered);

  };

  const getPerformanceColor = (name) => {

    const totalActivity = stats[name] ? stats[name].calls + stats[name].sms : 0;
    if (totalActivity > 20) return '#52c41a';
    if (totalActivity > 10) return '#faad14';
    return '#ff4d4f';

  };

  if (loading) {

    return (
      <div style={{ 
        backgroundColor: '#2a2b2b', 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <Spin size="large" tip="Cargando agentes..." />
      </div>
    );

  }

  if (error) {

    return (
      <div style={{ backgroundColor: '#2a2b2b', minHeight: '100vh', padding: '20px' }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ maxWidth: '800px', margin: '40px auto' }}
        />
      </div>
    );

  }

  return (

    <div style={{ 
      padding: '20px', 
      backgroundColor: '#2a2b2b', 
      minHeight: '70vh', 
      color: 'white'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Title level={2} style={{ 
          color: '#f49b33', 
          textAlign: 'center', 
          marginBottom: '20px'
        }}>
          <TeamOutlined /> Panel de Agentes
        </Title>

        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={8}>
            <Card bordered={false} style={{ 
              backgroundColor: '#353435',
              borderRadius: '8px'
            }}>
              <Statistic
                title={<span style={{ color: '#aaa' }}>Agentes Totales</span>}
                value={callers.length}
                prefix={<TeamOutlined style={{ color: '#f49b33' }} />}
                valueStyle={{ color: '#fff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card bordered={false} style={{ 
              backgroundColor: '#353435',
              borderRadius: '8px'
            }}>
              <Statistic
                title={<span style={{ color: '#aaa' }}>Top Performers</span>}
                value={callers.filter(name => stats[name]?.isTopPerformer).length}
                prefix={<StarOutlined style={{ color: 'gold' }} />}
                valueStyle={{ color: '#fff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card bordered={false} style={{ 
              backgroundColor: '#353435',
              borderRadius: '8px'
            }}>
              <Statistic
                title={<span style={{ color: '#aaa' }}>Actividad Hoy</span>}
                value={callers.reduce((sum, name) => sum + (stats[name]?.calls || 0) + (stats[name]?.sms || 0), 0)}
                prefix={<FireOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ color: '#fff' }}
              />
            </Card>
          </Col>
        </Row>

        <Divider style={{ borderColor: '#444' }} />

        <div style={{ marginBottom: '24px' }}>
          <Search
            placeholder="Buscar agente..."
            allowClear
            enterButton={<SearchOutlined style={{ color: '#f49b33' }} />}
            size="large"
            onChange={(e) => handleSearch(e.target.value)}
            style={{ 
              maxWidth: '500px', 
              margin: '0 auto',
              display: 'block'
            }}
          />
        </div>

        <Row gutter={[16, 16]}>
          {filteredCallers.map(name => (
            <Col key={name} xs={24} sm={12} lg={8}>
              <Card
                hoverable
                onClick={() => navigate(`/caller/${encodeURIComponent(name)}`)}
                style={{ 
                  backgroundColor: '#353435',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${getPerformanceColor(name)}`,
                  cursor: 'pointer',
                  height: '100%'
                }}
                bodyStyle={{ padding: '16px' }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '12px'
                }}>
                  <Avatar 
                    style={{ 
                      backgroundColor: getPerformanceColor(name),
                      marginRight: '12px'
                    }}
                  >
                    {name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Title level={4} style={{ 
                    color: '#fff', 
                    margin: 0,
                    flex: 1
                  }}>
                    {name}
                  </Title>
                  {stats[name]?.isTopPerformer && (
                    <Tag icon={<StarOutlined />} color="gold">
                      Top
                    </Tag>
                  )}
                </div>

                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between'
                }}>
                  <div>
                    <Text type="secondary" style={{ color: '#aaa', display: 'block' }}>
                      <PhoneOutlined style={{ color: '#f49b33', marginRight: '8px' }} />
                      Llamadas
                    </Text>
                    <Text strong style={{ color: '#fff', fontSize: '18px' }}>
                      {stats[name]?.calls || 0}
                    </Text>
                  </div>

                  <div>
                    <Text type="secondary" style={{ color: '#aaa', display: 'block' }}>
                      <MessageOutlined style={{ color: '#00c49f', marginRight: '8px' }} />
                      Mensajes
                    </Text>
                    <Text strong style={{ color: '#fff', fontSize: '18px' }}>
                      {stats[name]?.sms || 0}
                    </Text>
                  </div>

                  <div>
                    <Text type="secondary" style={{ color: '#aaa', display: 'block' }}>
                      Total
                    </Text>
                    <Text strong style={{ color: getPerformanceColor(name), fontSize: '18px' }}>
                      {(stats[name]?.calls || 0) + (stats[name]?.sms || 0)}
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {filteredCallers.length === 0 && (
          <Empty
            description={
                <span style={{ color: '#aaa', fontWeight: 'bold' }}>
                No se encontraron agentes
                </span>
            }
          />
        )}
      </div>
    </div>
  );
  
}

export default Dashboard;